import { useEffect, useState } from "react";
import api, { formatAxiosError } from "@/lib/api";
import { useDialog } from "@/context/DialogContext";
import { WarningCircle, Plus, PencilSimple, Trash, X, FloppyDisk, Lock } from "@phosphor-icons/react";

const EMPTY = { name: "", category: "", description_template: "", default_priority: "medium", suggested_assignee_role: "", checklist: [] };

export default function IssueTemplatesTab() {
  const { confirm } = useDialog();
  const [tpls, setTpls] = useState([]);
  const [dlg, setDlg] = useState(null);
  const [msg, setMsg] = useState({ type: "", text: "" });

  const flash = (type, text) => { setMsg({ type, text }); setTimeout(() => setMsg({ type: "", text: "" }), 4000); };
  const load = async () => {
    try { const r = await api.get("/issue-templates"); setTpls(r.data.templates || []); }
    catch (e) { flash("error", formatAxiosError(e)); }
  };
  useEffect(() => { load(); }, []);

  const del = async (t) => {
    const ok = await confirm({
      title: `Delete template "${t.name}"?`,
      description: "Any workflows using this template will need to select a different one.",
      confirmText: "Delete",
      variant: "destructive",
    });
    if (!ok) return;
    try { await api.delete(`/issue-templates/${t.id}`); await load(); flash("ok", "Deleted."); }
    catch (e) { flash("error", formatAxiosError(e)); }
  };

  return (
    <div className="max-w-5xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-2 mb-1"><WarningCircle size={16} weight="bold" className="text-[#002FA7]" />
            <h3 className="font-display text-xl font-black">Issue templates</h3></div>
          <p className="text-sm text-muted-foreground">Templates guide people through raising an issue with the right context.</p>
        </div>
        <button onClick={() => setDlg({ mode: "create", form: EMPTY })} className="btn-primary flex items-center gap-2" data-testid="new-issue-tpl-btn">
          <Plus size={14} weight="bold" /> New template
        </button>
      </div>
      {msg.text && <div className={`mb-4 text-xs px-3 py-2 border ${msg.type === "ok" ? "border-green-500 bg-green-50 text-green-700" : "border-red-500 bg-red-50 text-red-700"}`}>{msg.text}</div>}

      <div className="border border-border">
        <table className="w-full text-sm">
          <thead className="bg-muted border-b border-border">
            <tr>
              <th className="text-left px-3 py-2 font-semibold">Name</th>
              <th className="text-left px-3 py-2 font-semibold">Category</th>
              <th className="text-left px-3 py-2 font-semibold">Priority</th>
              <th className="text-left px-3 py-2 font-semibold">Suggested role</th>
              <th className="text-left px-3 py-2 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {tpls.map(t => (
              <tr key={t.id} className="border-b border-border">
                <td className="px-3 py-2 font-semibold">
                  {t.name}{t.is_default && <span className="ml-2 text-[10px] text-[#002FA7] inline-flex items-center gap-0.5"><Lock size={10} weight="bold" /> DEFAULT</span>}
                </td>
                <td className="px-3 py-2">{t.category || "—"}</td>
                <td className="px-3 py-2 uppercase text-xs">{t.default_priority}</td>
                <td className="px-3 py-2 text-xs">{t.suggested_assignee_role || "—"}</td>
                <td className="px-3 py-2 flex gap-1">
                  <button onClick={() => setDlg({ mode: "edit", tpl: t, form: { ...EMPTY, ...t } })} className="p-1 hover:bg-muted"><PencilSimple size={14} weight="bold" /></button>
                  {!t.is_default && <button onClick={() => del(t)} className="p-1 hover:bg-muted text-red-600"><Trash size={14} weight="bold" /></button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {dlg && <TplDialog dlg={dlg} onClose={() => setDlg(null)} onSaved={async () => { setDlg(null); await load(); flash("ok", "Saved."); }} />}
    </div>
  );
}

function TplDialog({ dlg, onClose, onSaved }) {
  const [form, setForm] = useState(dlg.form);
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  const [checkInput, setCheckInput] = useState("");

  const addCheck = () => {
    const s = checkInput.trim();
    if (!s) return;
    setForm({ ...form, checklist: [...form.checklist, s] });
    setCheckInput("");
  };

  const save = async () => {
    setErr(""); setSaving(true);
    try {
      if (dlg.mode === "create") await api.post("/issue-templates", form);
      else await api.patch(`/issue-templates/${dlg.tpl.id}`, form);
      await onSaved();
    } catch (e) { setErr(formatAxiosError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white border border-border max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-border flex items-center justify-between">
          <h3 className="font-display text-xl font-black">{dlg.mode === "create" ? "New issue template" : `Edit "${dlg.tpl.name}"`}</h3>
          <button onClick={onClose} className="p-1 hover:bg-muted"><X size={16} weight="bold" /></button>
        </div>
        <div className="p-6 space-y-3">
          {err && <div className="text-xs text-red-700 border border-red-500 bg-red-50 px-3 py-2">{err}</div>}
          <div className="grid grid-cols-2 gap-3">
            <F label="Name*"><input className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></F>
            <F label="Category"><input className="input" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} /></F>
            <F label="Default priority">
              <select className="input" value={form.default_priority} onChange={(e) => setForm({ ...form, default_priority: e.target.value })}>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="critical">Critical</option>
              </select>
            </F>
            <F label="Suggested assignee role"><input className="input" value={form.suggested_assignee_role} onChange={(e) => setForm({ ...form, suggested_assignee_role: e.target.value })} placeholder="e.g. hr, admin" /></F>
          </div>
          <F label="Description template"><textarea className="input min-h-[160px] font-mono text-xs" value={form.description_template} onChange={(e) => setForm({ ...form, description_template: e.target.value })} /></F>
          <F label="Checklist">
            <ol className="space-y-1 mb-2">
              {form.checklist.map((c, i) => (
                <li key={i} className="flex items-center gap-2 text-sm border border-border px-2 py-1">
                  <span className="flex-1">{i + 1}. {c}</span>
                  <button onClick={() => setForm({ ...form, checklist: form.checklist.filter((_, j) => j !== i) })} className="text-red-600 text-xs">Remove</button>
                </li>
              ))}
            </ol>
            <div className="flex gap-2">
              <input className="input flex-1" value={checkInput} onChange={(e) => setCheckInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCheck(); }}} placeholder="Add a step and press Enter" />
              <button onClick={addCheck} className="px-3 py-2 border border-border hover:bg-muted text-sm">Add</button>
            </div>
          </F>
        </div>
        <div className="px-6 py-4 border-t border-border flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-border hover:bg-muted">Cancel</button>
          <button onClick={save} disabled={saving || !form.name.trim()} className="btn-primary flex items-center gap-2">
            <FloppyDisk size={14} weight="bold" /> {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
function F({ label, children }) { return (<label className="block"><span className="block text-xs font-mono-mini uppercase tracking-widest text-muted-foreground mb-1">{label}</span>{children}</label>); }
